import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import Select from "react-select";

import CloseIcon from "../../assets/img/close-form-comer-modal.svg";
import ViewIcon from "../../assets/img/VER.svg";
import CustomModal from "../CustomModal";
import { getConfiguracion } from "../utils/get";
import { getPeriodosSince } from "../utils/monthNames";
import { customStylesCambioTanque, SingleOption } from "../utils/utils";
import {
  hideGreyBackground,
  scrollTo,
  showGreyBackground,
} from "../utils/windows";

const PROCESS_OPTIONS = [
  { label: "Cambio de tanque", value: "Cambio de tanque" },
  { label: "Ingreso pedido especial", value: "Ingreso pedido especial" },
];
const ALL_PROCESS_OPTION = { label: "Seleccionar", value: "" };
const HISTORY_PROCESS_OPTIONS = [ALL_PROCESS_OPTION, ...PROCESS_OPTIONS];
const SPECIAL_COMMENT_MAX_LENGTH = 500;

const ACCIONES_API_BASE = `${process.env.REACT_APP_CAMBIO_TANQUE_API}/acciones`;
const CAMBIO_TANQUE_ENDPOINTS = {
  CAMBIO_TANQUE: `${ACCIONES_API_BASE}/cambio_tanque`,
  CAMBIO_TANQUE_EXECUTION: `${ACCIONES_API_BASE}/cambio_tanque_execution`,
  CAMBIO_TANQUE_UPLOAD_FILE: `${ACCIONES_API_BASE}/cambio_tanque_upload_file`,
  CAMBIO_TANQUE_DOWNLOAD: `${ACCIONES_API_BASE}/cambio_tanque_download`,
  CAMBIO_TANQUE_SPECIAL_ORDER_HISTORY: `${ACCIONES_API_BASE}/cambio_tanque_special_order_history`,
};

const STATUS_LABELS = {
  PENDIENTE: "Pendiente",
  EN_PROCESO: "En proceso",
  EXITOSO: "Exitoso",
  FINALIZADO_CON_ERRORES: "Finalizado con errores",
  ERROR: "Error",
};
const HISTORY_STATUS_LABELS = {
  PENDIENTE: "Pendiente",
  EN_PROCESO: "En Ejecución",
  EXITOSO: "Ejecutado",
  FINALIZADO_CON_ERRORES: "Error",
  ERROR: "Error",
};
const OPEN_CLOSURE_STATUSES = ["ABIERTO", "RE ABIERTO", "REABIERTO", "EN REVISIÓN"];

const CambioTanque = ({
  token,
  pagador,
  instalaciones,
  callServiceHandler,
  displayCustomError,
}) => {
  const [activeTab, setActiveTab] = useState("upload");
  const [selectedProcessType, setSelectedProcessType] = useState(PROCESS_OPTIONS[0]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [historyItems, setHistoryItems] = useState([]);
  const [selectedUploadPeriod, setSelectedUploadPeriod] = useState();
  const [selectedHistoryPeriod, setSelectedHistoryPeriod] = useState();
  const [selectedHistoryProcessType, setSelectedHistoryProcessType] =
    useState(ALL_PROCESS_OPTION);
  const [historySearch, setHistorySearch] = useState("");
  const [selectedExecution, setSelectedExecution] = useState();
  const [displayModal, setDisplayModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalText, setModalText] = useState("");
  const [modalIcon, setModalIcon] = useState("success");
  const [allEds, setAllEds] = useState([]);
  const [selectedEds, setSelectedEds] = useState();
  const [edsPagadorMap, setEdsPagadorMap] = useState({});
  const [pagadorOptions, setPagadorOptions] = useState([]);
  const [selectedPagador, setSelectedPagador] = useState();
  const [selectedSpecialDate, setSelectedSpecialDate] = useState();
  const [openDateOptions, setOpenDateOptions] = useState([]);
  const [specialComment, setSpecialComment] = useState("");
  const [configuracion, setConfiguracion] = useState();
  const [tankAdjustments, setTankAdjustments] = useState({});
  const hiddenFileInput = useRef(null);
  const callServiceHandlerRef = useRef(callServiceHandler);
  const dinamoStationsRequested = useRef(false);
  const openDatesRequestKey = useRef("");
  const configurationRequestKey = useRef("");

  useEffect(() => {
    callServiceHandlerRef.current = callServiceHandler;
  }, [callServiceHandler]);

  const allPeriods = useMemo(() => getPeriodosSince("2024"), []);

  const activeTanks = useMemo(() => {
    return (
      configuracion?.TANQUES?.filter(
        (tanque) => tanque.ID !== "TAE_RE" && tanque.DESHABILITADO !== "SI"
      ) || []
    );
  }, [configuracion]);

  const specialOrderTankRows = useMemo(() => {
    return buildSpecialOrderTankRows(activeTanks);
  }, [activeTanks]);

  const filteredHistoryItems = useMemo(() => {
    const normalizedSearch = historySearch.trim().toLocaleLowerCase();
    if (!normalizedSearch) {
      return historyItems;
    }

    return historyItems.filter((item) => {
      const searchableValues = [
        item.process_type,
        item.created_at,
        formatDate(item.created_at),
        item.created_by,
        STATUS_LABELS[item.status] || item.status,
        item.file_name,
        item.metadata?.eds,
        item.metadata?.pagador,
      ];
      return searchableValues.some((value) =>
        String(value || "").toLocaleLowerCase().includes(normalizedSearch)
      );
    });
  }, [historyItems, historySearch]);

  useEffect(() => {
    if (allPeriods.length > 0) {
      setSelectedUploadPeriod(allPeriods[0]);
      setSelectedHistoryPeriod(allPeriods[0]);
    }
  }, [allPeriods]);

  useEffect(() => {
    if (!selectedExecution) {
      return undefined;
    }

    showGreyBackground();
    return () => hideGreyBackground();
  }, [selectedExecution]);

  useEffect(() => {
    if (Object.keys(edsPagadorMap).length > 0) {
      return;
    }

    const dinamoInstalaciones =
      instalaciones?.INSTALACIONES_DINAMO || instalaciones?.INSTALACIONES || [];
    const newOptions = dinamoInstalaciones.map((eds) => ({
      value: formatEdsValue(eds.ID_EDS),
      label: eds.CODIGO_DIRECCION || eds.NOMBRE || eds.ID_EDS,
    }));
    setAllEds(newOptions);
  }, [edsPagadorMap, instalaciones]);

  const fetchDinamoStations = useCallback(async () => {
    if (dinamoStationsRequested.current) {
      return;
    }
    dinamoStationsRequested.current = true;

    const url =
      `${process.env.REACT_APP_FLUCTUACIONES_API}/fluctuaciones/controles-diarios-dinamo-token?TIPO_REPORTE=LISTADO_ESTACIONES_ACTIVAS`;
    const response = await callServiceHandlerRef.current(url, "GET", null, false);
    if (response === "error") {
      dinamoStationsRequested.current = false;
      return;
    }

    const pagadorEstacion = response.data?.pagador_estacion || {};
    const nextMap = Object.entries(pagadorEstacion).reduce((accumulator, [pagadorId, edsList]) => {
      (edsList || []).forEach((edsId) => {
        const normalizedEds = formatEdsValue(edsId);
        if (!accumulator[normalizedEds]) {
          accumulator[normalizedEds] = [];
        }
        if (!accumulator[normalizedEds].includes(pagadorId)) {
          accumulator[normalizedEds].push(pagadorId);
        }
      });
      return accumulator;
    }, {});

    setEdsPagadorMap(nextMap);
    setAllEds(Object.keys(nextMap).sort().map((edsId) => ({
      value: edsId,
      label: edsId,
    })));
  }, []);

  useEffect(() => {
    if (selectedProcessType.value !== "Ingreso pedido especial") {
      return;
    }

    fetchDinamoStations();
  }, [fetchDinamoStations, selectedProcessType]);

  useEffect(() => {
    if (!selectedEds) {
      setPagadorOptions([]);
      setSelectedPagador();
      return;
    }

    const associatedPagadores = edsPagadorMap[selectedEds.value] || (pagador ? [pagador] : []);
    const nextOptions = associatedPagadores.map((pagadorId) => {
      const normalizedPagador = formatPagadorLabel(pagadorId);
      return {
        value: normalizedPagador,
        label: normalizedPagador,
      };
    });

    setPagadorOptions(nextOptions);
    setSelectedPagador(nextOptions.length === 1 ? nextOptions[0] : undefined);
  }, [edsPagadorMap, pagador, selectedEds]);

  const fetchOpenDates = useCallback(async () => {
    const selectedPagadorValue = selectedPagador?.value || "";
    const requestKey = `${selectedEds.value}-${selectedPagadorValue}`;
    if (openDatesRequestKey.current === requestKey) {
      return;
    }
    openDatesRequestKey.current = requestKey;

    const activationUrl =
      `${process.env.REACT_APP_FLUCTUACIONES_API}/fluctuaciones/cierre-diario-listar-periodos?ID_EDS=${selectedEds.value}&ID_PAGADOR=${selectedPagadorValue}`;

    const activationResponse = await callServiceHandlerRef.current(activationUrl, "GET", null, false);
    if (activationResponse === "error") {
      setOpenDateOptions([]);
      setSelectedSpecialDate();
      return;
    }

    const year = activationResponse.data?.año_activacion;
    const month = activationResponse.data?.mes_activacion;
    if (!year || !month) {
      setOpenDateOptions([]);
      setSelectedSpecialDate();
      return;
    }

    const period = `${String(month).padStart(2, "0")};${year}`;
    const summaryParams = new URLSearchParams({
      ID_EDS: selectedEds.value,
      PERIODO: period,
      ID_PAGADOR: selectedPagadorValue,
    });
    const summaryUrl =
      `${process.env.REACT_APP_FLUCTUACIONES_API}/fluctuaciones/resumen-cierre-diario?${summaryParams.toString()}`;

    const summaryResponse = await callServiceHandlerRef.current(summaryUrl, "GET", null, false);
    if (summaryResponse === "error") {
      setOpenDateOptions([]);
      setSelectedSpecialDate();
      return;
    }

    const nextOptions = (summaryResponse.data?.cierres || [])
      .filter((cierre) => OPEN_CLOSURE_STATUSES.includes(cierre.estado_cierre))
      .map((cierre) => ({
        value: cierre.fecha.replace(/-/g, ""),
        label: formatDisplayDate(cierre.fecha),
      }))
      .sort((left, right) => right.value.localeCompare(left.value));

    setOpenDateOptions(nextOptions);
    setSelectedSpecialDate((currentValue) =>
      nextOptions.find((option) => option.value === currentValue?.value)
    );
  }, [selectedEds, selectedPagador]);

  useEffect(() => {
    if (!selectedEds || !selectedPagador || selectedProcessType.value !== "Ingreso pedido especial") {
      setOpenDateOptions([]);
      setSelectedSpecialDate();
      setConfiguracion();
      setTankAdjustments({});
      openDatesRequestKey.current = "";
      return;
    }

    fetchOpenDates();
  }, [fetchOpenDates, selectedEds, selectedPagador, selectedProcessType]);

  useEffect(() => {
    if (selectedProcessType.value !== "Ingreso pedido especial") {
      return;
    }

    if (!selectedEds || !selectedPagador || !selectedSpecialDate) {
      setConfiguracion();
      setTankAdjustments({});
      configurationRequestKey.current = "";
      return;
    }

    const requestKey = `${selectedEds.value}-${selectedPagador.value}-${selectedSpecialDate.value}`;
    if (configurationRequestKey.current === requestKey) {
      return;
    }
    configurationRequestKey.current = requestKey;

    getConfiguracion(
      callServiceHandlerRef.current,
      selectedEds.value,
      setConfiguracion,
      selectedPagador.value
    );
  }, [selectedEds, selectedPagador, selectedSpecialDate, selectedProcessType]);

  useEffect(() => {
    setTankAdjustments((currentAdjustments) => {
      const nextAdjustments = {};
      specialOrderTankRows.forEach((row) => {
        if (row.type === "input") {
          nextAdjustments[row.tankId] = currentAdjustments[row.tankId] || "";
        }
      });
      return nextAdjustments;
    });
  }, [specialOrderTankRows]);

  const fetchHistory = useCallback(async (showLoader = true) => {
    if (!token || !selectedHistoryPeriod) {
      return;
    }

    let url = `${CAMBIO_TANQUE_ENDPOINTS.CAMBIO_TANQUE}?period=${encodeURIComponent(normalizePeriod(selectedHistoryPeriod.value))}`;
    if (selectedHistoryProcessType?.value) {
      url += `&process_type=${encodeURIComponent(selectedHistoryProcessType.value)}`;
    }

    const response = await callServiceHandlerRef.current(url, "GET", null, showLoader);
    if (response !== "error") {
      setHistoryItems(response.data.items || []);
    }
  }, [selectedHistoryPeriod, selectedHistoryProcessType, token]);

  useEffect(() => {
    if (!token) {
      return;
    }
    fetchHistory();
  }, [fetchHistory, token]);

  useEffect(() => {
    if (!token || !historyItems.some((item) => ["PENDIENTE", "EN_PROCESO"].includes(item.status))) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      fetchHistory(false);
    }, 15000);

    return () => window.clearInterval(intervalId);
  }, [fetchHistory, historyItems, token]);

  function normalizePeriod(periodValue) {
    if (!periodValue) {
      return "";
    }
    const parts = periodValue.split("%3B");
    if (parts.length === 2) {
      return `${parts[1]}${parts[0]}`;
    }
    return periodValue;
  }

  function openModal(title, text, icon = "success") {
    setModalTitle(title);
    setModalText(text);
    setModalIcon(icon);
    setDisplayModal(true);
    scrollTo(0, 0);
    showGreyBackground();
  }

  function closeModal() {
    setDisplayModal(false);
    hideGreyBackground();
  }

  function handleCargarArchivo() {
    if (!selectedUploadPeriod || !hiddenFileInput.current) {
      return;
    }

    hiddenFileInput.current.value = "";
    hiddenFileInput.current.click();
  }

  function handleFileChange(event) {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }

    const validExtension = file.name.toLowerCase().endsWith(".xlsx");
    if (!validExtension) {
      openModal("Error", "Solo se permite cargar archivos .xlsx.", "error");
      if (hiddenFileInput.current) {
        hiddenFileInput.current.value = "";
      }
      return;
    }

    setUploadedFile(file);
    submitExecution(file).finally(() => {
      if (hiddenFileInput.current) {
        hiddenFileInput.current.value = "";
      }
    });
  }

  function clearForm() {
    setUploadedFile(null);
    setSelectedEds(null);
    setSelectedPagador();
    setPagadorOptions([]);
    setSelectedSpecialDate();
    setConfiguracion();
    setSpecialComment("");
    setTankAdjustments((currentValues) =>
      Object.keys(currentValues).reduce((accumulator, key) => {
        accumulator[key] = "";
        return accumulator;
      }, {})
    );
    if (hiddenFileInput.current) {
      hiddenFileInput.current.value = "";
    }
  }

  function handleProcessTypeChange(option) {
    setSelectedProcessType(option);
    setSelectedEds(null);
    setSelectedPagador();
    setPagadorOptions([]);
    setUploadedFile(null);
    setSelectedSpecialDate();
    setOpenDateOptions([]);
    setConfiguracion();
    setTankAdjustments({});
  }

  async function requestUploadUrl(file) {
    const body = {
      file_name: file.name,
      content_type: file.type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      period: normalizePeriod(selectedUploadPeriod.value),
      process_type: selectedProcessType.value,
    };

    return callServiceHandler(
      CAMBIO_TANQUE_ENDPOINTS.CAMBIO_TANQUE_UPLOAD_FILE,
      "POST",
      JSON.stringify(body)
    );
  }

  async function uploadFileToS3(file, uploadConfig) {
    const uploadResponse = await fetch(uploadConfig.presigned, {
      method: "PUT",
      headers: uploadConfig.headers || {
        "Content-Type": file.type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      },
      body: file,
    });

    if (!uploadResponse.ok) {
      throw new Error(`S3 upload failed with status ${uploadResponse.status}`);
    }
  }

  function buildSpecialOrderPayload() {
    const normalizedDate = selectedSpecialDate?.value || "";
    const entradas = Object.entries(tankAdjustments).reduce((accumulator, [tankId, value]) => {
      const parsedValue = parseInt(value, 10);
      if (!Number.isNaN(parsedValue) && parsedValue !== 0) {
        accumulator[tankId] = parsedValue;
      }
      return accumulator;
    }, {});

    return {
      ACCION: "CAMBIO_MATERIAL_TANQUE_ESTACION",
      datos: {
        EDS: removeLeadingZeros(selectedEds?.value),
        PAGADOR: removeLeadingZeros(selectedPagador?.value),
        FECHA: normalizedDate,
        pedido: {
          comentario: specialComment || "Cambio de tanque material",
          entrada: entradas,
        },
      },
    };
  }

  async function handleExecutionSuccess(successTitle, successMessage) {
    clearForm();
    setActiveTab("history");
    await fetchHistory(false);
    openModal(successTitle, successMessage, "success");
  }

  async function createSpecialOrderHistory(responseData, requestBody) {
    const historyPayload = {
      datos: requestBody.datos,
      response: responseData,
    };

    return callServiceHandler(
      CAMBIO_TANQUE_ENDPOINTS.CAMBIO_TANQUE_SPECIAL_ORDER_HISTORY,
      "POST",
      JSON.stringify(historyPayload),
      false
    );
  }

  function handleSpecialOrderSuccess(historyItem) {

    clearForm();
    setHistoryItems((currentItems) => {
      const previousItem = currentItems.find((item) => isSameSpecialOrderHistoryItem(item, historyItem));
      const nextHistoryItem = {
        ...historyItem,
        created_by: historyItem.created_by === "-" ? previousItem?.created_by || "-" : historyItem.created_by,
      };
      return [
        nextHistoryItem,
        ...currentItems.filter((item) => !isSameSpecialOrderHistoryItem(item, nextHistoryItem)),
      ];
    });
    openModal("¡Operación exitosa!", "Proceso ejecutado con éxito.", "success");
  }

  function validateSpecialOrderInputs() {
    if (!selectedEds || !selectedPagador || !selectedSpecialDate) {
      openModal("Error", "Completa EDS, pagador y fecha para continuar.", "error");
      return null;
    }

    const body = buildSpecialOrderPayload();
    if (Object.keys(body.datos.pedido.entrada).length === 0) {
      openModal("Error", "Ingresa al menos un ajuste distinto de cero.", "error");
      return null;
    }

    return body;
  }

  async function submitSpecialOrderExecution() {
    const body = validateSpecialOrderInputs();
    if (!body) {
      return;
    }

    const url =
      `${process.env.REACT_APP_FLUCTUACIONES_API}/fluctuaciones/leer-datos-cierre-dinamo`;
    const response = await callServiceHandler(
      url,
      "POST",
      JSON.stringify(body)
    );
    if (response === "error") {
      return;
    }

    if (response.data?.status === "ERROR") {
      await fetchHistory(false);
      openModal(
        "¡Error!",
        response.data.message_error || "Error en la ejecución del proceso. Intente nuevamente.",
        "error"
      );
      return;
    }

    const historyResponse = await createSpecialOrderHistory(response.data, body);
    const historyItem = historyResponse !== "error"
      ? historyResponse.data
      : buildSpecialOrderHistoryItem(response.data, body, getEmailFromToken(token));
    handleSpecialOrderSuccess(historyItem);
  }

  function buildUploadExecutionPayload(file, uploadConfig) {
    return {
      file_name: file.name,
      file_key: uploadConfig.filename,
      content_type: file.type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      period: normalizePeriod(selectedUploadPeriod.value),
      process_type: selectedProcessType.value,
    };
  }

  async function submitUploadedFileExecution(file) {
    const uploadUrlResponse = await requestUploadUrl(file);
    if (uploadUrlResponse === "error") {
      return;
    }

    const uploadConfig = uploadUrlResponse.data;
    await uploadFileToS3(file, uploadConfig);

    const body = buildUploadExecutionPayload(file, uploadConfig);
    const response = await callServiceHandler(
      CAMBIO_TANQUE_ENDPOINTS.CAMBIO_TANQUE,
      "POST",
      JSON.stringify(body)
    );
    if (response === "error") {
      return;
    }

    await handleExecutionSuccess(
      "Operacion exitosa",
      "Archivo cargado. El proceso fue enviado correctamente."
    );
  }

  async function submitExecution(file = uploadedFile) {
    if (!token) {
      openModal("Error", "Aun no se ha cargado la sesion. Reintenta en unos segundos.", "error");
      return;
    }

    if (selectedProcessType.value === "Ingreso pedido especial") {
      await submitSpecialOrderExecution();
      return;
    }

    if (!file || !selectedUploadPeriod) {
      openModal("Error", "Selecciona un período y un archivo válido para continuar.", "error");
      return;
    }

    try {
      await submitUploadedFileExecution(file);
    } catch (error) {
      console.error("No se pudo cargar el archivo de cambio de tanque", error);
      openModal("Error", "No se pudo cargar el archivo. Reintenta nuevamente.", "error");
    }
  }

  async function handleDownload(executionId, artifactType) {
    if (!token) {
      displayCustomError("Error", "Aun no se ha cargado la sesion. Reintenta en unos segundos.", "error");
      return;
    }

    const url = `${CAMBIO_TANQUE_ENDPOINTS.CAMBIO_TANQUE_DOWNLOAD}?execution_id=${encodeURIComponent(executionId)}&artifact_type=${encodeURIComponent(artifactType)}`;
    const response = await callServiceHandler(url, "GET", null, false, (errorResponse) => {
      if (errorResponse?.userMessage) {
        displayCustomError("Error", errorResponse.userMessage, "error");
        return true;
      }
      return false;
    });

    if (response !== "error" && response.data.signed_url) {
      window.open(response.data.signed_url, "_blank", "noopener,noreferrer");
    }
  }

  async function openExecutionDetail(executionId) {
    if (!token) {
      displayCustomError("Error", "Aun no se ha cargado la sesion. Reintenta en unos segundos.", "error");
      return;
    }

    const localExecution = historyItems.find((item) => item.execution_id === executionId);
    if (localExecution?.source === "fluctuaciones") {
      setSelectedExecution(localExecution);
      scrollTo(0, 0);
      showGreyBackground();
      return;
    }

    const url =
      `${CAMBIO_TANQUE_ENDPOINTS.CAMBIO_TANQUE_EXECUTION}?execution_id=${encodeURIComponent(executionId)}`;
    const response = await callServiceHandler(url, "GET", null, false);
    if (response !== "error") {
      setSelectedExecution(response.data);
      scrollTo(0, 0);
      showGreyBackground();
    }
  }

  function closeExecutionDetail() {
    hideGreyBackground();
    setSelectedExecution();
  }

  function renderTankChangeDetail(execution) {
    return (
      <table className="cambio-tanque-detail-summary-table">
        <thead>
          <tr>
            <th>Tipo</th>
            <th>Dato</th>
          </tr>
        </thead>
        <tbody>
          <tr className="cambio-tanque-detail-summary-table__separator">
            <td colSpan="2" />
          </tr>
          <tr>
            <td>Cant. Actualizada</td>
            <td>{`${execution.summary?.updated_rows || 0}/${execution.summary?.total_rows || 0}`}</td>
          </tr>
          <tr>
            <td>Archivo Cargado</td>
            <td>
              <button
                type="button"
                className="link-button"
                onClick={() => handleDownload(execution.execution_id, "original")}
                disabled={!execution.original_s3_key}
              >
                Descargar Archivo
              </button>
            </td>
          </tr>
          <tr>
            <td>Errores</td>
            <td>
              <button
                type="button"
                className="link-button"
                onClick={() => handleDownload(execution.execution_id, "errors")}
                disabled={!execution.error_s3_key}
              >
                Descargar Errores
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  function renderSpecialOrderDetail(execution) {
    const metadata = execution.metadata || {};
    const entries = Object.entries(metadata.entrada || {});

    return (
      <table className="cambio-tanque-detail-summary-table">
        <thead>
          <tr>
            <th>Tipo</th>
            <th>Dato</th>
          </tr>
        </thead>
        <tbody>
          <tr className="cambio-tanque-detail-summary-table__separator">
            <td colSpan="2" />
          </tr>
          <tr>
            <td>EDS</td>
            <td>{metadata.eds || "-"}</td>
          </tr>
          <tr>
            <td>ID Pagador</td>
            <td>{metadata.pagador || "-"}</td>
          </tr>
          <tr>
            <td>Fecha del Pedido</td>
            <td>{formatDisplayDate(metadata.fecha)}</td>
          </tr>
          {entries.map(([tankId, adjustment]) => (
            <tr key={tankId}>
              <td>Tanque + Litros</td>
              <td>{`Tanque ${tankId} + ${adjustment}`}</td>
            </tr>
          ))}
          <tr>
            <td>Comentario</td>
            <td>{metadata.comentario || "-"}</td>
          </tr>
        </tbody>
      </table>
    );
  }

  function renderExecutionDetail() {
    if (!selectedExecution) {
      return null;
    }

    const isSpecialOrder = selectedExecution.process_type === "Ingreso pedido especial";
    const title = isSpecialOrder
      ? "Detalle Ingreso Pedido Especial"
      : "Detalle Cambio de Tanque";

    return (
      <div
        className="cambio-tanque-detail-overlay"
        role="presentation"
        onMouseDown={closeExecutionDetail}
      >
        <section
          className={`cambio-tanque-detail-modal ${
            isSpecialOrder ? "cambio-tanque-detail-modal--special" : ""
          }`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cambio-tanque-detail-title"
          onMouseDown={(event) => event.stopPropagation()}
        >
          <header className="cambio-tanque-detail-modal__header">
            <div>
              <h3 id="cambio-tanque-detail-title">{title}</h3>
              <p>{formatDate(selectedExecution.created_at)}</p>
            </div>
            <button
              type="button"
              className="cambio-tanque-detail-modal__close"
              onClick={closeExecutionDetail}
              aria-label="Cerrar detalle"
            >
              <img src={CloseIcon} alt="" />
            </button>
          </header>
          <div className="cambio-tanque-detail-modal__body">
            {isSpecialOrder
              ? renderSpecialOrderDetail(selectedExecution)
              : renderTankChangeDetail(selectedExecution)}
          </div>
        </section>
      </div>
    );
  }

  function renderSpecialOrderTankRow(row) {
    if (row.type === "separator") {
      return (
        <tr key={row.key} className="cambio-tanque-special-table__separator">
          <td colSpan="3" />
        </tr>
      );
    }

    return (
      <tr
        key={row.key}
        className={[
          "data",
          row.isChild ? "cambio-tanque-special-table__sifon-child" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <td>{row.label}</td>
        <td>{row.material}</td>
        <td>
          <input
            className="cambio-tanque-input cambio-tanque-input--number"
            type="number"
            value={tankAdjustments[row.tankId] || ""}
            onChange={(event) =>
              setTankAdjustments({
                ...tankAdjustments,
                [row.tankId]: event.target.value,
              })
            }
          />
        </td>
      </tr>
    );
  }

  function renderSpecialOrderTab() {
    return (
      <div className="cambio-tanque-layout">
        <div className="cambio-tanque-card">
          <div className="cambio-tanque-form-grid cambio-tanque-form-grid--top">
            <div className="filter">
              <div className="filter-title required-field">TIPO DE PROCESO</div>
              <Select
                value={selectedProcessType}
                onChange={handleProcessTypeChange}
                options={PROCESS_OPTIONS}
                styles={customStylesCambioTanque}
                components={{ Option: SingleOption }}
              />
            </div>
            <div className="filter">
              <div className="filter-title required-field">EDS</div>
              <Select
                value={selectedEds}
                onChange={setSelectedEds}
                options={allEds}
                styles={customStylesCambioTanque}
                components={{ Option: SingleOption }}
                placeholder="Seleccionar"
                isSearchable
              />
            </div>
            <div className="filter">
              <div className="filter-title required-field">ID DE PAGADOR</div>
              <Select
                value={selectedPagador}
                onChange={setSelectedPagador}
                options={pagadorOptions}
                styles={customStylesCambioTanque}
                components={{ Option: SingleOption }}
                placeholder="Seleccionar"
                isDisabled={!selectedEds || pagadorOptions.length === 0}
                isSearchable
              />
            </div>
            <div className="filter">
              <div className="filter-title required-field">FECHA</div>
              <Select
                value={selectedSpecialDate}
                onChange={setSelectedSpecialDate}
                options={openDateOptions}
                styles={customStylesCambioTanque}
                components={{ Option: SingleOption }}
                placeholder="Seleccionar"
                isDisabled={!selectedEds || !selectedPagador}
              />
            </div>
          </div>

          <div className="cambio-tanque-special-panel">
            <div className="cambio-tanque-special-header">
              <div className="cambio-tanque-special-title">Datos Pedido Especial</div>
            </div>
            <div className="cambio-tanque-special-help">
              Usa valores negativos para salida y positivos para entrada.
            </div>
            <div className="cambio-tanque-special-table">
              <table className="cambio-tanque-special-table__grid">
                <colgroup>
                  <col className="cambio-tanque-special-table__col-tank" />
                  <col className="cambio-tanque-special-table__col-material" />
                  <col className="cambio-tanque-special-table__col-adjustment" />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col">
                      <div className="th-data">
                        <div className="th-text">Tanque</div>
                      </div>
                    </th>
                    <th scope="col">
                      <div className="th-data">
                        <div className="th-text">Material</div>
                      </div>
                    </th>
                    <th scope="col">
                      <div className="th-data">
                        <div className="th-text">Ajuste</div>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="table-separator-row-border-top">
                    <td colSpan="3" />
                  </tr>
                  {specialOrderTankRows.map((row) => renderSpecialOrderTankRow(row))}
                  {activeTanks.length === 0 && (
                    <tr className="data">
                      <td colSpan="3" className="cambio-tanque-empty-state">
                        {selectedEds && openDateOptions.length === 0
                          ? "La EDS no tiene fechas abiertas disponibles en cierre mensual."
                          : "Selecciona una fecha abierta para cargar la configuración de tanques de la EDS."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="cambio-tanque-special-comment">
              <div className="filter-title">COMENTARIO</div>
              <textarea
                className="cambio-tanque-special-comment__input"
                value={specialComment}
                onChange={(event) => setSpecialComment(event.target.value)}
                placeholder="Ingrese aquí sus comentarios"
                maxLength={SPECIAL_COMMENT_MAX_LENGTH}
              />
              <div className="cambio-tanque-special-comment__counter">
                {specialComment.length}/{SPECIAL_COMMENT_MAX_LENGTH}
              </div>
            </div>

            <div className="cambio-tanque-actions-row">
              <button
                type="button"
                className="cambio-tanque-btn cambio-tanque-btn--secondary"
                onClick={clearForm}
              >
                Limpiar
              </button>
              <button
                type="button"
                className="cambio-tanque-btn cambio-tanque-btn--primary"
                onClick={() => submitExecution()}
                disabled={!selectedEds || !selectedSpecialDate || activeTanks.length === 0}
              >
                Ingresar Pedido Especial
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderUploadTab() {
    if (selectedProcessType.value === "Ingreso pedido especial") {
      return renderSpecialOrderTab();
    }

    return (
      <div className="cambio-tanque-layout">
        <div className="cambio-tanque-card">
          <div className="cambio-tanque-upload-panel">
            <div className="cambio-tanque-upload-toolbar">
              <div className="cambio-tanque-form-grid cambio-tanque-form-grid--compact cambio-tanque-form-grid--inline">
                <div className="filter">
                  <div className="filter-title required-field">TIPO DE PROCESO</div>
                  <Select
                    value={selectedProcessType}
                    onChange={handleProcessTypeChange}
                    options={PROCESS_OPTIONS}
                    styles={customStylesCambioTanque}
                    components={{ Option: SingleOption }}
                  />
                </div>
                <div className="filter">
                  <div className="filter-title">PERIODO</div>
                  <Select
                    value={selectedUploadPeriod}
                    onChange={setSelectedUploadPeriod}
                    options={allPeriods}
                    styles={customStylesCambioTanque}
                    components={{ Option: SingleOption }}
                  />
                </div>
              </div>

              <div className="cambio-tanque-actions-row cambio-tanque-actions-row--inline">
                <button
                  type="button"
                  className="cambio-tanque-btn cambio-tanque-btn--secondary"
                  onClick={clearForm}
                >
                  Limpiar
                </button>
                <button
                  type="button"
                  className="cambio-tanque-btn cambio-tanque-btn--primary"
                  onClick={handleCargarArchivo}
                  disabled={!selectedUploadPeriod}
                >
                  Cargar Archivo
                </button>
              </div>
            </div>

            <input
              type="file"
              ref={hiddenFileInput}
              onChange={handleFileChange}
              style={{ display: "none" }}
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            />
          </div>
        </div>
      </div>
    );
  }

  function renderHistoryTab() {
    return (
      <div className="cambio-tanque-layout">
        <div className="cambio-tanque-card">
          <div className="cambio-tanque-history-toolbar">
            <div className="cambio-tanque-form-grid cambio-tanque-form-grid--history">
              <div className="filter">
                <div className="filter-title">TIPO DE PROCESO</div>
                <Select
                  value={selectedHistoryProcessType}
                  onChange={setSelectedHistoryProcessType}
                  options={HISTORY_PROCESS_OPTIONS}
                  styles={customStylesCambioTanque}
                  components={{ Option: SingleOption }}
                  placeholder="Seleccionar"
                />
              </div>
              <div className="filter">
                <div className="filter-title required-field">PERIODO</div>
                <Select
                  value={selectedHistoryPeriod}
                  onChange={setSelectedHistoryPeriod}
                  options={allPeriods}
                  styles={customStylesCambioTanque}
                  components={{ Option: SingleOption }}
                  placeholder="Seleccionar"
                />
              </div>
            </div>
            <input
              type="search"
              className="cambio-tanque-history-search"
              value={historySearch}
              onChange={(event) => setHistorySearch(event.target.value)}
              placeholder="Buscar..."
              aria-label="Buscar en histórico de procesos"
            />
          </div>

          <div className="cambio-tanque-history-table">
            <table
              className="cambio-tanque-history-table__grid"
              aria-label="Listado de ejecución de procesos de cambio de tanque"
            >
              <colgroup>
                <col className="width-process" />
                <col className="width-date" />
                <col className="width-owner" />
                <col className="width-status" />
                <col className="width-detail" />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col"><div className="th-data"><div className="th-text">Tipo de Proceso</div></div></th>
                  <th scope="col"><div className="th-data"><div className="th-text">Fecha y Hora Ejecución</div></div></th>
                  <th scope="col"><div className="th-data"><div className="th-text">Responsable</div></div></th>
                  <th scope="col"><div className="th-data"><div className="th-text">Estado</div></div></th>
                  <th scope="col"><div className="th-data"><div className="th-text">Detalle</div></div></th>
                </tr>
              </thead>
              <tbody>
                <tr className="table-separator-row-border-top">
                  <td colSpan="5" />
                </tr>
                {filteredHistoryItems.length === 0 && (
                  <tr className="data">
                    <td colSpan="5" className="cambio-tanque-empty-state">
                      {historyItems.length === 0
                        ? "No hay ejecuciones para el período seleccionado."
                        : "No hay resultados para la búsqueda ingresada."}
                    </td>
                  </tr>
                )}
                {filteredHistoryItems.map((item) => {
                  return (
                    <React.Fragment key={item.execution_id}>
                      <tr className="data">
                        <td className="align-left">{item.process_type}</td>
                        <td className="align-left">{formatDate(item.created_at)}</td>
                        <td className="align-left">{item.created_by}</td>
                        <td className="align-left">
                          <span className={`cambio-tanque-status cambio-tanque-status--${(item.status || "").toLowerCase()}`}>
                            {HISTORY_STATUS_LABELS[item.status] || STATUS_LABELS[item.status] || item.status}
                          </span>
                        </td>
                        <td className="align-left cambio-tanque-detail-cell">
                          <button
                            type="button"
                            className="cambio-tanque-detail-button"
                            onClick={() => openExecutionDetail(item.execution_id)}
                            aria-label="Ver detalle"
                          >
                            <img src={ViewIcon} alt="" />
                          </button>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="administracion-dinamo cambio-tanque-page">
      <div className="title-container">
        <div className="title">
          <div className="view-title">Administracion</div>
          <div className="view-title-separator">|</div>
          <div className="view-subtitle">Cambio de tanque</div>
        </div>
      </div>

      <hr className="title-separator-line" />

      <div className="administracion-filters-section cambio-tanque-tabs">
        <div className={`selector ${activeTab === "upload" ? "active" : ""}`}>
          <div className="selector-text" onClick={() => setActiveTab("upload")}>
            Ejecutar Proceso
          </div>
        </div>
        <div className={`selector ${activeTab === "history" ? "active" : ""}`}>
          <div className="selector-text" onClick={() => setActiveTab("history")}>
            Histórico de Ejecución Proceso
          </div>
        </div>
      </div>

      {activeTab === "upload" ? renderUploadTab() : renderHistoryTab()}

      <CustomModal
        displayModal={displayModal}
        closeModal={closeModal}
        modalTitle={modalTitle}
        modalText={modalText}
        modalIcon={modalIcon}
        acceptFunc={closeModal}
      />
      {selectedExecution &&
        ReactDOM.createPortal(renderExecutionDetail(), document.body)}
    </div>
  );
};

function formatDate(dateValue) {
  if (!dateValue) {
    return "-";
  }
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return dateValue;
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year} - ${hours}:${minutes}`;
}

function formatDisplayDate(dateValue) {
  if (!dateValue) {
    return "";
  }

  const parts = dateValue.split("-");
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }

  if (dateValue.length === 8) {
    return `${dateValue.slice(6, 8)}/${dateValue.slice(4, 6)}/${dateValue.slice(0, 4)}`;
  }

  return dateValue;
}

function formatPagadorLabel(pagadorId) {
  const normalizedPagador = String(pagadorId || "");
  if (/^\d+$/.test(normalizedPagador) && normalizedPagador.length < 10) {
    return normalizedPagador.padStart(10, "0");
  }
  return normalizedPagador;
}

function formatEdsValue(edsId) {
  const normalizedEds = String(edsId || "");
  if (/^\d+$/.test(normalizedEds) && normalizedEds.length < 10) {
    return normalizedEds.padStart(10, "0");
  }
  return normalizedEds;
}

function removeLeadingZeros(value) {
  const normalizedValue = String(value || "");
  if (/^\d+$/.test(normalizedValue)) {
    return normalizedValue.replace(/^0+/, "") || "0";
  }
  return normalizedValue;
}

function getEmailFromToken(token) {
  try {
    const payload = String(token || "").split(".")[1];
    if (!payload) {
      return "";
    }
    const normalizedPayload = payload
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(payload.length / 4) * 4, "=");
    const parsedPayload = JSON.parse(window.atob(normalizedPayload));
    return parsedPayload.email || parsedPayload["cognito:username"] || "";
  } catch (error) {
    return "";
  }
}

function buildSpecialOrderHistoryItem(responseData, requestBody, fallbackResponsible = "") {
  const registro = responseData?.registro_historial || {};
  const datos = requestBody.datos || {};
  const pedido = registro.pedido || datos.pedido || {};
  const fechaPedido = registro.fecha_pedido || datos.FECHA || "";
  const eds = registro.eds || datos.EDS || "";
  const pagador = registro.pagador || datos.PAGADOR || "";

  return {
    execution_id: registro.SK || `pedido-especial-${pagador}-${eds}-${fechaPedido}`,
    process_type: "Ingreso pedido especial",
    period: fechaPedido.slice(0, 6),
    status: "EXITOSO",
    created_at: registro.fecha_hora_ingreso || new Date().toISOString(),
    updated_at: registro.fecha_hora_ingreso || new Date().toISOString(),
    finished_at: registro.fecha_hora_ingreso || new Date().toISOString(),
    created_by: registro.responsable || registro.email || fallbackResponsible || "-",
    file_name: `pedido-especial-${eds}-${fechaPedido}.json`,
    source: "fluctuaciones",
    metadata: {
      eds,
      pagador,
      fecha: fechaPedido,
      comentario: pedido.comentario || "",
      entrada: pedido.entrada || {},
    },
    summary: {
      total_rows: Object.keys(pedido.entrada || {}).length,
      updated_rows: Object.keys(pedido.entrada || {}).length,
      error_rows: 0,
      not_found_rows: 0,
    },
  };
}

function isSameSpecialOrderHistoryItem(item, historyItem) {
  if (item.process_type !== "Ingreso pedido especial") {
    return false;
  }

  const itemMetadata = item.metadata || {};
  return (
    removeLeadingZeros(itemMetadata.eds) === removeLeadingZeros(historyItem.metadata.eds) &&
    removeLeadingZeros(itemMetadata.pagador) === removeLeadingZeros(historyItem.metadata.pagador) &&
    itemMetadata.fecha === historyItem.metadata.fecha
  );
}

function buildSpecialOrderTankRows(tanks) {
  const tankById = tanks.reduce((accumulator, tank) => {
    accumulator[tank.ID] = tank;
    return accumulator;
  }, {});
  const renderedTankIds = new Set();

  return tanks.reduce((rows, tank) => {
    if (renderedTankIds.has(tank.ID) || isSiphonChildTank(tank, tankById)) {
      return rows;
    }

    const group = getSiphonGroup(tank, tankById);
    group.forEach((groupTank) => renderedTankIds.add(groupTank.ID));

    if (group.length === 1) {
      rows.push(buildTankInputRow(tank));
      return rows;
    }

    const groupIds = group.map((groupTank) => groupTank.ID);
    const groupLabel = groupIds.join(" - ");
    rows.push({ type: "separator", key: `separator-${tank.ID}` });
    rows.push(buildTankInputRow(tank, false, `Tanque ${groupLabel}`, groupIds.join("-")));
    return rows;
  }, []);
}

function buildTankInputRow(tank, isChild = false, label = `Tanque ${tank.ID}`, tankId = tank.ID) {
  return {
    type: "input",
    key: `tank-${tankId}`,
    tankId,
    label,
    material: tank.MATERIAL,
    isChild,
  };
}

function isSiphonChildTank(tank, tankById) {
  const numericTankId = parseInt(tank.ID, 10);
  if (Number.isNaN(numericTankId)) {
    return false;
  }

  const groupIds = getConfiguredSiphonIds(tank).filter((tankId) => tankById[tankId]);
  return groupIds.some((siphonId) => {
    const numericSiphonId = parseInt(siphonId, 10);
    return !Number.isNaN(numericSiphonId) && numericTankId > numericSiphonId;
  });
}

function getSiphonGroup(tank, tankById) {
  const groupIds = [tank.ID, ...getConfiguredSiphonIds(tank)].filter((tankId) => tankById[tankId]);
  const uniqueIds = [...new Set(groupIds)];
  return uniqueIds
    .map((tankId) => tankById[tankId])
    .sort((left, right) => compareTankIds(left.ID, right.ID));
}

function getConfiguredSiphonIds(tank) {
  return ["SIFON_1", "SIFON_2", "SIFON_3"]
    .map((key) => String(tank[key] || "").trim())
    .filter(Boolean);
}

function compareTankIds(leftId, rightId) {
  const numericLeft = parseInt(leftId, 10);
  const numericRight = parseInt(rightId, 10);
  if (!Number.isNaN(numericLeft) && !Number.isNaN(numericRight)) {
    return numericLeft - numericRight;
  }
  return String(leftId).localeCompare(String(rightId));
}

export default CambioTanque;
